<template>
    <transition name="fade" mode="out-in">
        <loader v-if="loading"></loader>
        <form v-else class="quotetool__step">
            <div class="container">
                <div class="row quotetool__step-heading-container">
                    <div class="col-xs-12 col-md-6">
                        <h2 class="quotetool__step-heading">
                            {{ labels.stepThreeTitle }}
                        </h2>
                    </div>
                    <div class="col-xs-12 col-md-6">
                        <span class="quotetool__step-sub-heading">
                            {{ labels.stepThreeEstimateUsageLabel }} <strong>{{ annualUsage }} m<sup>3</sup></strong>
                        </span>
                    </div>
                </div>
                <div class="quotetool__step-table-container">
                    <table class="responsive-table">
                        <thead>
                            <tr>
                                <th>{{ labels.stepThreeTarrifHeading }}</th>
                                <th>{{ labels.stepThreeEstimateAnnualCostHeading }}*</th>
                                <th>{{ labels.stepThreeTotalEstimatedSavingsHeading }}**</th>
                                <th>{{ labels.stepThreeKeyTermsHeading }}</th>
                                <th>{{ labels.stepThreePaymentExtrasHeading }}</th>
                                <th>{{ labels.stepThreeNextStepHeading }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <!-- 1 YEAR -->
                            <tr>
                                <!-- Tariff -->
                                <td>
                                    <p><strong>1 Year</strong> Source for Business Plan</p>
                                </td>

                                <!-- Estimated annual cost -->
                                <td :data-label="labels.stepThreeEstimateAnnualCostHeading + '*'">
                                    <p class="highlight--grey">{{ oneYearTotal | toCurrency(2) }}</p>
                                    <quote-popup length="1"
                                                 @open="handlePopupOpen"
                                                 ref="popup1"
                                                 :calculation="oneYearCalculation"
                                                 :open-label="labels.stepThreeSeeCalculationOpenedLabel"
                                                 :close-label="labels.stepThreeSeeCalculationClosedLabel"></quote-popup>
                                </td>

                                <!-- Total estimated saving -->
                                <td data-label="Total estimated saving**">
                                    <p v-if="oneYearSaving < 0" class="highlight--red u-mr-7">+ {{ Math.abs(oneYearSaving) | toCurrency(2) }}</p>
                                    <p v-else class="highlight--green u-mr-7">- {{ Math.abs(oneYearSaving) | toCurrency(2) }}</p>
                                    <p>vs default tariff</p>
                                </td>

                                <!-- Key contract terms -->
                                <td data-label="Key contract terms">
                                    <ul>
                                        <li>Ends {{ oneYearDate }}</li>
                                        <li>Bi-annual bills issued</li>
                                        <li>Bi-annual meter reads</li>
                                    </ul>
                                </td>

                                <!-- Payment & extras -->
                                <td data-label="Payment & extras">
                                    <ul>
                                        <li>Pay monthly</li>
                                        <li>14 day payment terms</li>
                                    </ul>
                                </td>

                                <!-- Next step -->
                                <td data-label="Next step">
                                    <button @click.prevent="chooseContract(1)">{{ labels.stepThreeChooseContractButton }}</button>
                                </td>
                            </tr>

                            <!-- 3 YEAR -->
                            <tr>
                                <!-- Tariff -->
                                <td>
                                    <p><strong>3 Year</strong> Source for Business Plan</p>
                                </td>

                                <!-- Estimated annual cost -->
                                <td :data-label="labels.stepThreeEstimateAnnualCostHeading + '*'">
                                    <p class="highlight--grey">{{ threeYearTotal | toCurrency(2) }}</p>
                                    <quote-popup length="3"
                                                 @open="handlePopupOpen"
                                                 ref="popup3"
                                                 :calculation="threeYearCalculation"
                                                 :open-label="labels.stepThreeSeeCalculationOpenedLabel"
                                                 :close-label="labels.stepThreeSeeCalculationClosedLabel"></quote-popup>
                                </td>

                                <!-- Total estimated saving -->
                                <td data-label="Total estimated saving**">
                                    <p v-if="threeYearSaving < 0" class="highlight--red u-mr-7">+ {{ Math.abs(threeYearSaving) | toCurrency(2) }}</p>
                                    <p v-else class="highlight--green u-mr-7">- {{ Math.abs(threeYearSaving) | toCurrency(2) }}</p>
                                    <p>vs default tariff</p>
                                </td>

                                <!-- Key contract terms -->
                                <td data-label="Key contract terms">
                                    <ul>
                                        <li>Ends {{ threeYearDate }}</li>
                                        <li>Bi-annual bills issued</li>
                                        <li>Bi-annual meter reads</li>
                                    </ul>
                                </td>

                                <!-- Payment & extras -->
                                <td data-label="Payment & extras">
                                    <ul>
                                        <li>{{ threeYearDiscount | toCurrency(2) }} loyalty discount</li>
                                        <li>Pay monthly</li>
                                        <li>14 day payment terms</li>
                                    </ul>
                                </td>

                                <!-- Next step -->
                                <td data-label="Next step">
                                    <button @click.prevent="chooseContract(3)">{{ labels.stepThreeChooseContractButton }}</button>
                                </td>
                            </tr>

                            <!-- 5 YEAR -->
                            <tr>
                                <!-- Tariff -->
                                <td>
                                    <p><strong>5 Year</strong> Source for Business Plan</p>
                                </td>

                                <!-- Estimated annual cost -->
                                <td :data-label="labels.stepThreeEstimateAnnualCostHeading + '*'">
                                    <p class="highlight--grey">{{ fiveYearTotal | toCurrency(2) }}</p>
                                    <quote-popup length="5"
                                                 @open="handlePopupOpen"
                                                 ref="popup5"
                                                 :calculation="fiveYearCalculation"
                                                 :open-label="labels.stepThreeSeeCalculationOpenedLabel"
                                                 :close-label="labels.stepThreeSeeCalculationClosedLabel"></quote-popup>
                                </td>

                                <!-- Total estimated saving -->
                                <td data-label="Total estimated saving**">
                                    <p v-if="fiveYearSaving < 0" class="highlight--red u-mr-7">+ {{ Math.abs(fiveYearSaving) | toCurrency(2) }}</p>
                                    <p v-else class="highlight--green u-mr-7">- {{ Math.abs(fiveYearSaving) | toCurrency(2) }}</p>
                                    <p>vs default tariff</p>
                                </td>

                                <!-- Key contract terms -->
                                <td data-label="Key contract terms">
                                    <ul>
                                        <li>Ends {{ fiveYearDate }}</li>
                                        <li>Bi-annual bills issued</li>
                                        <li>Bi-annual meter reads</li>
                                    </ul>
                                </td>

                                <!-- Payment & extras -->
                                <td data-label="Payment & extras">
                                    <ul>
                                        <li>{{ fiveYearDiscount | toCurrency(2) }} loyalty discount</li>
                                        <li>Pay monthly</li>
                                        <li>14 day payment terms</li>
                                    </ul>
                                </td>

                                <!-- Next step -->
                                <td data-label="Next step">
                                    <button @click.prevent="chooseContract(5)">{{ labels.stepThreeChooseContractButton }}</button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <!-- Compliance text -->
                    <p class="quotetool__step-table-compliance-text" v-html="labels.stepThreeComplianceText"></p>
                    <p class="quotetool__step-table-compliance-text" v-html="labels.stepThreeAdditionalComplianceText"></p>
                </div>
            </div>

            <!--|| 4 col footer section--light-blue ||-->
            <section class="section--light-blue">
                <div class="container container--4-col">
                    <div class="row">
                        <div class="col-xs-12 text-center">
                            <h2>{{ labels.stepThreeCustomerAccessHeading }}</h2>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-xs-12 col-sm-6 col-lg-3">
                            <div class="container--4-col__col-wrapper">
                                <img src="/dist/img/shield-test.png" />
                                <h3>{{ labels.stepThreeCustomerAccessColumnOneHeading }}</h3>
                                <p>{{ labels.stepThreeCustomerAccessColumnOneContent }}</p>
                            </div>
                        </div>
                        <div class="col-xs-12 col-sm-6 col-lg-3">
                            <div class="container--4-col__col-wrapper">
                                <img src="/dist/img/understand-icon.png" />
                                <h3>{{ labels.stepThreeCustomerAccessColumnTwoHeading }}</h3>
                                <p>{{ labels.stepThreeCustomerAccessColumnTwoContent }}</p>
                            </div>
                        </div>
                        <div class="col-xs-12 col-sm-6 col-lg-3">
                            <div class="container--4-col__col-wrapper">
                                <img src="/dist/img/tap-icon.png" />
                                <h3>{{ labels.stepThreeCustomerAccessColumnThreeHeading }}</h3>
                                <p>{{ labels.stepThreeCustomerAccessColumnThreeContent }}</p>
                            </div>
                        </div>
                        <div class="col-xs-12 col-sm-6 col-lg-3">
                            <div class="container--4-col__col-wrapper">
                                <img src="/dist/img/enhance-icon.png" />
                                <h3>{{ labels.stepThreeCustomerAccessColumnFourHeading }}</h3>
                                <p>{{ labels.stepThreeCustomerAccessColumnFourContent }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </form>
    </transition>
</template>

<script>
    import { createHelpers } from 'vuex-map-fields';

    import StepNavigation from '../StepNavigation.vue';
    import QuotePopup from '../QuotePopup.vue';
    import Loader from '../Loader.vue';

    import moment from 'moment';
    import { mapActions, mapMutations, mapState } from 'vuex';

    const { mapFields } = createHelpers({
        getterType: 'quoteStep/getField',
        mutationType: 'quoteStep/updateField',
    });

    export default {
        components: {
            StepNavigation,
            QuotePopup,
            Loader,
        },

        data() {
            return {
                loading: false,
            }
        },

        computed: {
            ...mapFields([
                'annualUsage',
                'quotes',
                'oneYearSaving',
                'oneYearTotal',
                'threeYearDiscount',
                'threeYearTotal',
                'threeYearSaving',
                'fiveYearDiscount',
                'fiveYearTotal',
                'fiveYearSaving',
                'oneYearCalculation',
                'threeYearCalculation',
                'fiveYearCalculation',
            ]),

            ...mapState('microcopy', [
                'labels',
            ]),

            oneYearDate() {
                return moment().add(1, 'years').format('DD/MM/YY');
            },

            threeYearDate() {
                return moment().add(3, 'years').format('DD/MM/YY');
            },

            fiveYearDate() {
                return moment().add(5, 'years').format('DD/MM/YY');
            }
        },

        methods: {
            /**
             * Select specified contract
             * @param id - contract id
             */
            chooseContract(period) {
                this.loading = true;
                this.submit(period).then(() => {
                    this.updateTotalCharge(+period);
                    this.loading = false;
                })
            },

            handlePopupOpen({ length }) {
                for (let quoteLength = 1; quoteLength < 7; quoteLength += 2) {
                    if (quoteLength !== +length) {
                        this.$refs[`popup${quoteLength}`].modalOpen = false;
                    }
                }
            },

            ...mapActions('quoteStep', [
                'submit'
            ]),

            ...mapMutations('quoteStep', [
                'updateTotalCharge'
            ])
        }
    }
</script>